import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { PlusIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui';
const FIELD_CLASS = 'min-h-[var(--unity-hit-target)] min-w-[12rem] flex-1 rounded-[var(--unity-radius)] border border-border bg-[var(--unity-color-elevated)] px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
export function StageToolbar({ programTitle, columns, busy, editing, onEditingChange, onAddStage, onReorder, }) {
    const [adding, setAdding] = useState(false);
    const [draftTitle, setDraftTitle] = useState('');
    const [localBusy, setLocalBusy] = useState(false);
    const inputRef = useRef(null);
    const canEdit = Boolean(onAddStage && onReorder);
    const blocked = busy || localBusy;
    useEffect(() => {
        if (!adding) {
            return;
        }
        inputRef.current?.focus();
    }, [adding]);
    useEffect(() => {
        setAdding(false);
        setDraftTitle('');
    }, [programTitle]);
    async function run(action) {
        setLocalBusy(true);
        try {
            await action();
        }
        finally {
            setLocalBusy(false);
        }
    }
    function openAdd() {
        if (!onAddStage)
            return;
        setAdding(true);
        setDraftTitle('');
    }
    function cancelAdd() {
        setAdding(false);
        setDraftTitle('');
    }
    async function submitAdd(event) {
        event?.preventDefault();
        const title = draftTitle.trim();
        if (!title || blocked || !onAddStage) {
            return;
        }
        await run(async () => {
            await onAddStage(title);
            setAdding(false);
            setDraftTitle('');
        });
    }
    function onAddKeyDown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            cancelAdd();
        }
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", children: [_jsxs("h2", { className: "text-sm font-semibold uppercase tracking-wide text-muted-foreground", children: ["\u0428\u0430\u0433\u0438: ", programTitle] }), canEdit ? (_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { type: "button", variant: editing ? 'default' : 'ghost', size: "sm", disabled: blocked || columns.length < 2, onClick: () => onEditingChange(!editing), children: editing ? 'Готово' : 'Редактировать' }), adding ? (_jsxs("form", { className: "flex min-w-[16rem] flex-1 items-center gap-2 sm:flex-none", onSubmit: (event) => void submitAdd(event), children: [_jsx("input", { ref: inputRef, className: FIELD_CLASS, value: draftTitle, maxLength: 80, placeholder: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u044D\u0442\u0430\u043F\u0430", disabled: blocked, "aria-label": "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u043D\u043E\u0432\u043E\u0433\u043E \u044D\u0442\u0430\u043F\u0430", onChange: (event) => setDraftTitle(event.target.value), onKeyDown: onAddKeyDown }), _jsx(Button, { type: "submit", size: "sm", disabled: blocked || draftTitle.trim().length === 0, children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", disabled: blocked, onClick: cancelAdd, children: "\u041E\u0442\u043C\u0435\u043D\u0430" })] })) : (_jsx(Button, { type: "button", variant: "outline", size: "icon", "aria-label": "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u044D\u0442\u0430\u043F", disabled: blocked, onClick: openAdd, children: _jsx(PlusIcon, { className: "size-4", "aria-hidden": true }) }))] })) : null] }), editing ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "\u0421\u0442\u0440\u0435\u043B\u043A\u0430\u043C\u0438 \u0432 \u0448\u0430\u043F\u043A\u0435 \u043A\u043E\u043B\u043E\u043D\u043A\u0438 \u043C\u0435\u043D\u044F\u0439\u0442\u0435 \u043F\u043E\u0440\u044F\u0434\u043E\u043A \u044D\u0442\u0430\u043F\u043E\u0432. \u041F\u043E\u0440\u044F\u0434\u043E\u043A \u0441\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u0442\u0441\u044F \u0441\u0440\u0430\u0437\u0443." })) : null] }));
}
export function ColumnStageControls({ index, columnCount, title, busy, onMove, }) {
    return (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx(Button, { type: "button", variant: "outline", size: "sm", "aria-label": `Сдвинуть «${title}» влево`, disabled: busy || index === 0, onClick: () => void onMove(index, -1), children: "\u2190" }), _jsx(Button, { type: "button", variant: "outline", size: "sm", "aria-label": `Сдвинуть «${title}» вправо`, disabled: busy || index === columnCount - 1, onClick: () => void onMove(index, 1), children: "\u2192" })] }));
}
